import { ZendeskController } from '~/ogm-hub';
import { getSession } from './Session';
import { getStorage, removeStorage, setStorage } from './sessionStorageHelper';

export const zendeskStorageKey = `zendeskClassicToken`;

interface IZendeskConfig {
	externalId: string;
	zendeskUrl: string;
	zendeskToken: string;
	isZendeskEnabled: boolean;
	customerId: number;
	contactId: number;
	error: boolean;
}

const cleanToken = (token: string): string => token.replace(/"/g, '').trim();

export async function configureZendeskWidget(maxAge: number): Promise<void> {
	const session = getSession();
	if (!session?.userId) {
		return;
	}

	const custContNum = session.externalId ?? '';
	const customerId = session.customer?.id;
	const contactId = session.userId;

	let config: IZendeskConfig = JSON.parse(getStorage(zendeskStorageKey));

	const needsRefresh =
		!config ||
		config.customerId !== customerId ||
		config.contactId !== contactId ||
		config.externalId !== custContNum ||
		config.error ||
		!config.zendeskUrl?.trim();

	if (needsRefresh) {
		removeStorage(zendeskStorageKey);
		config = await loadZendeskConfig(custContNum, customerId, contactId);
		setStorage(
			zendeskStorageKey,
			JSON.stringify(config),
			config.isZendeskEnabled ? maxAge : maxAge / 2
		);
	}

	if (!config.isZendeskEnabled) {
		return;
	}

	injectZendeskWidget(
		cleanToken(config.zendeskToken),
		config.zendeskUrl.trim()
	);
}

async function loadZendeskConfig(
	custContNum: string,
	customerId: number,
	contactId: number
): Promise<IZendeskConfig> {
	const response = await ZendeskController.GetZendeskToken(custContNum);

	if (!response.ok) {
		return {
			externalId: '',
			zendeskUrl: '',
			zendeskToken: '',
			isZendeskEnabled: false,
			customerId,
			contactId,
			error: true,
		};
	}

	const zendeskToken = cleanToken(response.value.widgetToken);
	const zendeskUrl = response.value.widgetUrl.trim();

	return {
		externalId: custContNum,
		zendeskUrl,
		zendeskToken,
		isZendeskEnabled: zendeskUrl !== '' && zendeskToken !== '',
		customerId,
		contactId,
		error: false,
	};
}

async function refreshZendeskToken(): Promise<string> {
	const session = getSession();
	const response = await ZendeskController.GetZendeskToken(
		session?.externalId ?? ''
	);

	if (!response.ok) {
		return '';
	}

	return cleanToken(response.value.widgetToken);
}

const injectZendeskWidget = (
	initialToken: string,
	zendeskUrl: string
): void => {
	(window as Window & { zESettings?: unknown }).zESettings = {
		webWidget: {
			authenticate: {
				jwtFn: (callback: (jwt: string) => void) => {
					refreshZendeskToken().then((token) =>
						callback(token || initialToken)
					);
				},
			},
		},
	};

	if (document.getElementById('ze-snippet')) {
		showClassicWidget();
		return;
	}

	const script = document.createElement('script');
	script.id = 'ze-snippet';
	script.src = zendeskUrl;
	script.async = true;
	script.onload = showClassicWidget;
	document.body.appendChild(script);
};

const showClassicWidget = (): void => {
	const zE = window['zE'];
	if (typeof zE !== 'function') {
		return;
	}

	zE(() => {
		try {
			zE('webWidget', 'show');
		} catch {
			// Classic Web Widget may not be enabled on this account.
		}

		window['isZenDeskConfigured'] = true;
	});
};
